import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Calendar, Clock, ChevronRight, Sparkles, Flame, TrendingDown, TrendingUp, Play, ExternalLink, Users } from 'lucide-react';
import QuickEntryTile from './QuickEntryTile';
import { ClientActionItemsPanel } from './ClientActionItemsPanel';
import { useWeightProgress } from '../../hooks/useWeightProgress';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useIsMobile';
import { HomeTodayCard } from './mobile/HomeTodayCard';
import { HomeWeeklyHero } from './mobile/HomeWeeklyHero';

/**
 * ClientHome - New client dashboard home page
 *
 * Reuses staff context patterns with client-specific filtering:
 * - Shows only logged-in user's data
 * - Welcome banner with phase/streak info
 * - Next session tile (filtered to this client)
 * - Action items panel (uses ClientActionItemsPanel component)
 */

interface Appointment {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  status: string;
}

interface WeeklySummaryData {
  daysWithData: number;
  averages: {
    calories: number;
  };
}

interface ClientHomeProps {
  profileBanner?: React.ReactNode;
}

const ClientHome: React.FC<ClientHomeProps> = ({ profileBanner }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [upcomingSessions, setUpcomingSessions] = useState<Appointment[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [clientPhase, setClientPhase] = useState<number>(1);
  const [weeklySummary, setWeeklySummary] = useState<WeeklySummaryData | null>(null);
  const { data: weightData, loading: weightLoading, error: weightError } = useWeightProgress();
  const streak = weightData?.streak?.current ?? 0;
  const isMobile = useIsMobile();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Fetch upcoming sessions - 2 week lookahead, max 3 (matching staff context)
  useEffect(() => {
    const fetchUpcomingSessions = async () => {
      if (!user?.id) return;

      try {
        const today = new Date();
        const twoWeeksLater = new Date(today);
        twoWeeksLater.setDate(twoWeeksLater.getDate() + 14);

        const startDate = today.toISOString().split('T')[0];
        const endDate = twoWeeksLater.toISOString().split('T')[0];

        const response = await fetch(
          `/api/appointments?attendee_id=${user.id}&start_date=${startDate}&end_date=${endDate}&status=scheduled`,
          { headers: getAuthHeaders() }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.appointments?.length > 0) {
            // Sort by start time and take first 3 (matching staff behavior)
            const sorted = data.appointments
              .sort((a: Appointment, b: Appointment) =>
                new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
              )
              .slice(0, 3);
            setUpcomingSessions(sorted);
          }
        }
      } catch (err) {
        console.error('[ClientHome] Error fetching sessions:', err);
      } finally {
        setSessionsLoading(false);
      }
    };

    fetchUpcomingSessions();
  }, [user?.id]);

  // Fetch client profile for phase data (uses /api/users/me which includes client_phase)
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;

      try {
        const response = await fetch(
          `/api/users/me`,
          { headers: getAuthHeaders() }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setClientPhase(data.user.client_phase || 1);
          }
        }
      } catch (err) {
        console.error('[ClientHome] Error fetching profile:', err);
      }
    };

    fetchProfile();
  }, [user?.id]);

  // Fetch weekly nutrition summary for "This Week" card
  useEffect(() => {
    const fetchWeeklySummary = async () => {
      try {
        const response = await fetch('/api/nutrition/weekly-summary', {
          headers: getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setWeeklySummary({
              daysWithData: data.daysWithData,
              averages: { calories: data.averages?.calories || 0 }
            });
          }
        }
      } catch (err) {
        console.error('[ClientHome] Error fetching weekly summary:', err);
      }
    };

    fetchWeeklySummary();
  }, []);

  // Format date for display
  const formatSessionDate = (dateStr: string): { day: string; date: string; time: string } => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Reset for comparison
    const dateCompare = new Date(date);
    dateCompare.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);

    let dayLabel: string;
    if (dateCompare.getTime() === today.getTime()) {
      dayLabel = 'TODAY';
    } else if (dateCompare.getTime() === tomorrow.getTime()) {
      dayLabel = 'TMRW';
    } else {
      dayLabel = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
    }

    return {
      day: dayLabel,
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
    };
  };

  // Get user's display name
  const displayName = user?.first_name || user?.username || 'there';

  // Phase is fetched from profile via useEffect above

  return (
    <div className="client-home">
      {/* Compact Header Bar */}
      <div className="home-header">
        <div className="header-greeting">
          <h1>Welcome back, {displayName}!</h1>
        </div>
        <div className="header-status">
          <span className="status-pill phase">Phase {clientPhase}</span>
          {streak > 0 && (
            <span className="status-pill streak">
              <Sparkles size={12} />
              {streak} day streak
            </span>
          )}
        </div>
      </div>

      {/* Profile incomplete banner (passed from parent) */}
      {profileBanner}

      {/* Mobile: 3-zone Home (SP3). Zone 1 weekly hero + Zone 2 today card.
          Old desktop dashboard still renders below as a working safety net. */}
      {isMobile && (
        <div className="mobile-home-zones">
          <HomeWeeklyHero
            weightData={weightData}
            weightLoading={weightLoading}
            weightError={weightError}
            daysLogged={weeklySummary?.daysWithData ?? null}
            avgCalories={weeklySummary?.averages?.calories ?? null}
          />
          <HomeTodayCard />
        </div>
      )}

      {/* Three-Column Grid (matching staff layout) */}
      <div className="home-grid">
        {/* Left Column */}
        <div className="column column-left">
          <div className="card this-week-home-card" onClick={() => navigate('/client/progress')} style={{ cursor: 'pointer' }}>
            <div className="section-header">
              <h3><Sparkles size={16} /> This Week</h3>
            </div>
            <div className="this-week-stats">
              {/* Weight change */}
              <div className="tw-stat">
                {weightData?.weeklyChange !== null && weightData?.weeklyChange !== undefined ? (
                  <>
                    <span className={`tw-stat-value ${weightData.weeklyChange < 0 ? 'loss' : weightData.weeklyChange > 0 ? 'gain' : ''}`}>
                      {weightData.weeklyChange < 0 ? (
                        <><TrendingDown size={14} /> {Math.abs(weightData.weeklyChange).toFixed(1)} lbs</>
                      ) : weightData.weeklyChange > 0 ? (
                        <><TrendingUp size={14} /> +{weightData.weeklyChange.toFixed(1)} lbs</>
                      ) : (
                        <>0.0 lbs</>
                      )}
                    </span>
                  </>
                ) : (
                  <span className="tw-stat-value muted">-- lbs</span>
                )}
              </div>

              {/* Days logged */}
              <div className="tw-stat">
                <span className="tw-stat-value">
                  {weeklySummary ? `${weeklySummary.daysWithData} of 7` : '-- of 7'} days logged
                </span>
              </div>

              {/* Streak */}
              {streak > 0 && (
                <div className="tw-stat">
                  <span className="tw-stat-value streak-line">
                    {streak} week streak <Flame size={14} className="flame-inline" />
                  </span>
                </div>
              )}

              {/* Avg calories */}
              {weeklySummary && weeklySummary.averages.calories > 0 && (
                <div className="tw-stat">
                  <span className="tw-stat-value">
                    {weeklySummary.averages.calories.toLocaleString()} avg cal/day
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Quick Entry Tile */}
          <QuickEntryTile />
        </div>

        {/* Center Column */}
        <div className="column column-center">
          <div className="card card-full community-card">
            <div className="section-header">
              <h3>Community & Resources</h3>
              <span className="sample-badge">Sample Content</span>
            </div>

            {/* Announcements */}
            <div className="community-section">
              <h4 className="community-section-title">Announcements</h4>
              <div className="announcement-item">
                <div className="announcement-date">Feb 28</div>
                <div className="announcement-content">
                  <strong>New meal prep guide available!</strong>
                  <p>Check out our simplified 5-day meal prep system designed for busy schedules...</p>
                </div>
              </div>
              <div className="announcement-item">
                <div className="announcement-date">Feb 25</div>
                <div className="announcement-content">
                  <strong>March challenge starts Monday</strong>
                  <p>Join the 30-day consistency challenge — log meals every day and earn your streak badge...</p>
                </div>
              </div>
            </div>

            {/* Resources */}
            <div className="community-section">
              <h4 className="community-section-title">Resources</h4>
              <div className="resources-grid">
                <div className="resource-card">
                  <div className="resource-thumbnail" style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}>
                    <Play size={24} />
                  </div>
                  <span className="resource-title">Understanding Macros</span>
                </div>
                <div className="resource-card">
                  <div className="resource-thumbnail" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                    <Play size={24} />
                  </div>
                  <span className="resource-title">Hydration & Recovery</span>
                </div>
              </div>
            </div>

            {/* Community Links */}
            <div className="community-section">
              <h4 className="community-section-title">Connect</h4>
              <div className="social-links">
                <div className="social-link">
                  <Users size={14} />
                  <span>Facebook Group</span>
                  <ExternalLink size={12} />
                </div>
                <div className="social-link">
                  <Sparkles size={14} />
                  <span>Instagram</span>
                  <ExternalLink size={12} />
                </div>
                <div className="social-link">
                  <Calendar size={14} />
                  <span>Support</span>
                  <ExternalLink size={12} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Matching staff context layout */}
        <div className="column column-right">
          {/* Upcoming Sessions (matching staff UpcomingSessionsBlock) */}
          <div className="card upcoming-sessions-block">
            <div className="section-header">
              <h3>
                <Calendar size={16} /> Upcoming Sessions
                {upcomingSessions.length > 0 && (
                  <span className="count">({upcomingSessions.length})</span>
                )}
              </h3>
            </div>

            {sessionsLoading ? (
              <div className="sessions-loading">Loading sessions...</div>
            ) : upcomingSessions.length > 0 ? (
              <div className="upcoming-sessions-list">
                {upcomingSessions.map(session => {
                  const formatted = formatSessionDate(session.start_time);
                  const isToday = formatted.day === 'TODAY';
                  return (
                    <div key={session.id} className="session-card">
                      <div className={`session-date-badge ${isToday ? 'today' : ''}`}>
                        <span className="date-relative">{formatted.day}</span>
                        <span className="date-day">{new Date(session.start_time).getDate()}</span>
                      </div>
                      <div className="session-info">
                        <div className="session-title">
                          <Calendar size={14} className="session-type-icon" />
                          <span>{session.title}</span>
                        </div>
                        <div className="session-meta">
                          <span className="session-time">
                            <Clock size={12} />
                            {formatted.time}
                          </span>
                          <span className="session-duration">({session.duration_minutes} min)</span>
                        </div>
                      </div>
                      <ChevronRight size={16} className="session-arrow" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="sessions-empty">No upcoming sessions in the next 2 weeks</div>
            )}
          </div>

          {/* Action Items Panel (reuses staff pattern with client modifications) */}
          <ClientActionItemsPanel />
        </div>
      </div>

      <style>{`
        .client-home {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        /* Compact Header Bar */
        .home-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 24px;
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-green));
          margin: -24px -24px 16px -24px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .header-greeting h1 {
          font-size: 20px;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .header-status {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .status-pill {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          color: white;
        }

        .status-pill.streak svg {
          color: #fbbf24;
        }

        /* Three-Column Grid (matching staff layout) */
        .home-grid {
          display: grid;
          grid-template-columns: 260px 1fr 320px;
          gap: 20px;
          padding: 24px;
          flex: 1;
          align-content: start;
        }

        .column {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .column-right {
          align-self: start;
        }

        /* Card Base Styles - gray cards on black background */
        .card {
          background: var(--bg-secondary);
          border-radius: var(--radius-large);
          padding: 20px;
          border: 1px solid var(--border-secondary);
        }

        /* Section Header (matching staff pattern) */
        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .section-header h3 {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }

        .section-header h3 svg {
          color: var(--accent-blue);
        }

        .section-header h3 .count {
          color: var(--text-secondary);
          font-weight: 400;
        }

        /* This Week Home Card */
        .this-week-home-card:hover {
          border-color: var(--accent-blue);
        }

        .this-week-stats {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .tw-stat {
          display: flex;
          align-items: center;
        }

        .tw-stat-value {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary);
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .tw-stat-value.loss {
          color: var(--accent-green);
        }

        .tw-stat-value.gain {
          color: var(--status-red, #ef4444);
        }

        .tw-stat-value.muted {
          color: var(--text-tertiary);
        }

        .tw-stat-value.streak-line {
          color: #f59e0b;
        }

        .flame-inline {
          color: #f59e0b;
        }

        /* Community Card */
        .sample-badge {
          font-size: 10px;
          color: var(--text-tertiary);
          background: var(--bg-tertiary);
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 500;
        }

        .community-section {
          margin-bottom: 16px;
        }

        .community-section:last-child {
          margin-bottom: 0;
        }

        .community-section-title {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0 0 8px 0;
        }

        .announcement-item {
          display: flex;
          gap: 10px;
          padding: 8px 10px;
          background: var(--bg-primary);
          border-radius: var(--radius-small);
          margin-bottom: 6px;
        }

        .announcement-date {
          font-size: 11px;
          color: var(--text-tertiary);
          white-space: nowrap;
          padding-top: 2px;
          min-width: 44px;
        }

        .announcement-content strong {
          font-size: 13px;
          color: var(--text-primary);
          display: block;
          margin-bottom: 2px;
        }

        .announcement-content p {
          font-size: 12px;
          color: var(--text-secondary);
          margin: 0;
          line-height: 1.4;
        }

        .resources-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
        }

        .resource-card {
          display: flex;
          flex-direction: column;
          gap: 6px;
          cursor: pointer;
        }

        .resource-thumbnail {
          height: 60px;
          border-radius: var(--radius-small);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          opacity: 0.85;
        }

        .resource-card:hover .resource-thumbnail {
          opacity: 1;
        }

        .resource-title {
          font-size: 12px;
          color: var(--text-primary);
          font-weight: 500;
        }

        .social-links {
          display: flex;
          gap: 8px;
        }

        .social-link {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          font-size: 12px;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .social-link:hover {
          border-color: var(--accent-blue);
          color: var(--text-primary);
        }

        /* Upcoming Sessions Block (matching staff UpcomingSessionsBlock) */
        .upcoming-sessions-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .session-card {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 10px 12px;
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .session-card:hover {
          border-color: var(--accent-blue);
          background: var(--bg-secondary);
        }

        .session-date-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 52px;
          flex-shrink: 0;
          padding: 6px 4px;
          background: var(--bg-secondary);
          border-radius: var(--radius-small);
          text-align: center;
        }

        .session-date-badge.today {
          background: var(--accent-blue);
          color: white;
        }

        .session-date-badge.today .date-relative,
        .session-date-badge.today .date-day {
          color: white;
        }

        .date-relative {
          font-size: 10px;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .date-day {
          font-size: 14px;
          font-weight: 700;
          color: var(--text-primary);
          line-height: 1.2;
        }

        .session-info {
          flex: 1;
          min-width: 0;
        }

        .session-title {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .session-type-icon {
          color: var(--accent-blue);
          flex-shrink: 0;
        }

        .session-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--text-secondary);
        }

        .session-meta svg {
          opacity: 0.7;
        }

        .session-time {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .session-duration {
          color: var(--text-tertiary);
        }

        .session-arrow {
          color: var(--text-tertiary);
          opacity: 0;
          transition: opacity 0.15s ease;
        }

        .session-card:hover .session-arrow {
          opacity: 1;
        }

        .sessions-empty,
        .sessions-loading {
          padding: 16px;
          text-align: center;
          color: var(--text-secondary);
          font-size: 13px;
        }

        /* Action Items Panel (matching staff ActionItemsPanel) */
        .action-items-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .action-item-row {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 8px 10px;
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .action-item-row:hover {
          border-color: var(--accent-blue);
          background: var(--bg-secondary);
        }

        .item-checkbox {
          color: var(--text-tertiary);
          flex-shrink: 0;
          margin-top: 2px;
        }

        .item-title {
          flex: 1;
          font-size: 13px;
          color: var(--text-primary);
          line-height: 1.4;
        }

        .loading-state {
          padding: 16px;
          text-align: center;
          color: var(--text-tertiary);
          font-size: 13px;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 16px;
          text-align: center;
          color: var(--text-secondary);
          font-size: 13px;
        }

        .empty-state.success {
          color: var(--accent-green);
        }

        .empty-state.success svg {
          opacity: 0.7;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .home-grid {
            grid-template-columns: 1fr 320px;
          }

          .column-left {
            display: none;
          }

          .column-center {
            grid-column: 1;
          }
        }

        @media (max-width: 768px) {
          .home-header {
            flex-direction: column;
            align-items: flex-start;
            padding: 16px;
          }

          .home-grid {
            grid-template-columns: 1fr;
            padding: 16px;
          }

          .column-left {
            display: flex;
          }

          .column-right {
            order: -1;
          }
        }
      `}</style>
    </div>
  );
};

export default ClientHome;
